import { Alert, Box, useTheme } from "@mui/material";
import { useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";

import { useAuth } from "@/providers";
import { PageLayout } from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";

import { DeviceHeader } from "../DeviceHeader";
import { DeviceTabs } from "../DeviceTabs";

import type { DeviceContext } from "./useDeviceContext";

/**
 * Parent layout for the `/devices/:deviceId` route tree. Renders the page
 * chrome (header + tab bar) and delegates the active tab's body to
 * `<Outlet>`. Publishes `{ deviceId, org }` as outlet context so descendant
 * tab routes can read them via `useDeviceContext` instead of re-deriving
 * from `useParams` / `useAuth`.
 *
 * Renders an error alert in place of the chrome when the URL is missing
 * `:deviceId`; downstream errors thrown inside a tab are caught by the
 * shared `<ErrorBoundary>` wrapping the outlet.
 */
export function Device() {
  const { deviceId } = useParams();
  const theme = useTheme();
  const { currentOrganization: org } = useAuth();

  // Memoize so the object handed to <Outlet context={...}> keeps a stable
  // reference across renders whose inputs haven't changed. Without this,
  // every render of <Device /> would publish a fresh context object and
  // invalidate referential-equality checks (memo deps, effect deps) in
  // descendant tabs that consume the bundle through useDeviceContext.
  const outletContext = useMemo<DeviceContext | null>(() => {
    if (!deviceId) {
      return null;
    }
    return {
      deviceId,
      org,
    };
  }, [deviceId, org]);

  // Guard: a route hit without `:deviceId` can't render the device chrome
  // or hydrate child tabs; surface the misconfiguration inline rather than
  // letting downstream hooks fire with an undefined id.
  if (!deviceId) {
    return (
      <PageLayout title={`Roboto - Device`}>
        <Alert severity="error">Missing device id in path</Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={`Roboto - Device - ${deviceId}`}
      headerContentLeft={<DeviceHeader deviceId={deviceId} />}
    >
      <ErrorBoundary
        fallback={(props) => {
          if (props.error instanceof Error) {
            return <Alert severity="error">{props.error.message}</Alert>;
          }

          return (
            <Alert severity="error">
              Something went wrong. Please contact{" "}
              <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
              problem persists.
            </Alert>
          );
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
          }}
        >
          <DeviceTabs deviceId={deviceId} />
          <Outlet context={outletContext} />
        </Box>
      </ErrorBoundary>
    </PageLayout>
  );
}
